import { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, InputAdornment, TextField, Typography } from "@mui/material";
import { makeSxStyles } from "ui";
import { UseMutateFunction } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import { isNumber } from "lodash";

export interface EditableCellProps {
  id: string;
  idHandle?: string;
  prop: string;
  label: string | number;
  append: string;
  mutate: UseMutateFunction<any, unknown, any, unknown>;
  refetch?: () => void;
  readonly?: boolean;
}

const EditableCell: FC<EditableCellProps> = (props) => {
  const {
    id,
    prop,
    label,
    append,
    mutate,
    refetch,
    readonly,
    idHandle = "id",
  } = props;
  const styles = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);

  const { values, handleChange, submitForm, dirty, setFieldValue } = useFormik({
    initialValues: {
      [prop]: label,
    },
    onSubmit: (values) => {
      mutate(
        {
          pathParams: { [idHandle]: id },
          body: values,
        },
        {
          onSuccess: () => {
            setIsEditing(false);
            toast.success("Updated Successfully");
            refetch && refetch();
          },
        },
      );
    },
  });

  useEffect(() => {
    setFieldValue(prop, label);
  }, [label]);

  function handleBlur(): void {
    if (dirty) {
      submitForm();
    } else {
      setIsEditing(false);
    }
  }

  function handleClick(): void {
    if (readonly) {
      return;
    }
    setIsEditing(true);
  }

  useLayoutEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  return (
    <Box sx={styles.section}>
      {isEditing ? (
        <Box>
          <TextField
            sx={styles.control}
            id={prop}
            type="number"
            margin="none"
            variant="outlined"
            value={values[prop]}
            onBlur={handleBlur}
            onChange={handleChange}
            fullWidth
            InputProps={{
              inputRef,
              endAdornment: (
                <InputAdornment position="end">{append}</InputAdornment>
              ),
              sx: styles.input,
            }}
          />
        </Box>
      ) : (
        <Box onClick={handleClick}>
          <Typography variant="caption">
            {isNumber(values[prop]) ? `${values[prop]} ${append}` : "N/A"}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeSxStyles(() => ({
  section: {
    position: "relative",
    cursor: "pointer",
  },
  control: {
    marginLeft: "-14px",
  },
  input: {
    input: {
      height: "0px",
      fontSize: "0.75rem",
      lineHeight: "0.85rem",
    },
  },
}));

export default EditableCell;
